import React, { useCallback, useEffect, useMemo } from "react";
import { useI18n } from "../../../hooks/i18n-hooks";
import VStack from "../../../ui_atoms/VStack";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import TextField from "../../TextField";
import HStack from "../../../ui_atoms/HStack";
import Gap from "../../../ui_atoms/Gap";
import {
    calculatePayrollBreakdownMaxWorkDays,
    FormData,
    PayrollBreakdownEditorProps,
    useValidateNumber,
} from "./PayrollBreakdownEditor";
import DropdownField from "../../DropdownField";
import AbsenceList from "./AbsenceList";
import AbsenceCreateButton from "./AbsenceCreateButton";
import { BodyMediumText, BodySmallText, LinkSmallText, TitleMediumText } from "../../FormattedText";
import PayrollBreakdownCommon from "./PayrollBreakdownCommon";
import PayrollBreakdownTotalInfo from "./PayrollBreakdownTotalInfo";
import PayrollBreakdownMonthlyInfo from "./PayrollBreakdownMonthlyInfo";
import PayrollBreakdownAbsences from "./PayrollBreakdownAbsenses";
import { useTheme } from "../../../hooks/use-theme";
import {
    GET_MISSION_OVERTIME_STATUS,
    GET_MISSION_VACATION_ACCUMULATION_MINUTES_FOR_PAYROLL_BREAKDOWN,
} from "../../../graphql/timesheet";
import { useQuery } from "@apollo/client";
import { SHORT_DATE, useDateFormatter } from "../../../hooks/date";
import FullWidthLine from "../../FullWidthLine";
import { DynamicFees } from "./DynamicFees";
import { useDeepCompareEffect } from "../../../hooks/use-deep-compare-effect";
import { useChainInputFocus } from "../../../hooks/use-chain-input-focus";
import PayrollBreakdownBonusEffectiveFeesOvertimePaid from "./PayrollBreakdownBonusEffectiveFeesOvertimePaid";
import { useIntercom } from "../../../hooks/intercom";

const displayDefaultHoursValue = (x?: number | undefined | null): string => {
    if (x === undefined || x === null) {
        return "";
    }
    const hours = x / 60;
    if (hours % 1 === 0) {
        return hours.toString();
    } else {
        return hours.toFixed(2);
    }
};

const displayDefaultNumberValue = (x?: string | undefined | null): string => {
    if (x === undefined || x === null) {
        return "";
    }
    return `${x}`;
};

const displayDefaultPlusMinusValue = (x?: string | undefined | null): "plus" | "minus" => {
    if (x === undefined || x === null) {
        return "plus";
    }
    if (Number(x) < 0) {
        return "minus";
    }
    return "plus";
};

const PayrollBreakdownEditorMonthly: React.FC<PayrollBreakdownEditorProps> = ({
    payrollBreakdown,
    vacationStatus,
    onValidData,
    onChange,
    needWorkDays,
    isFromTimesheet,
    timeSheetsNeedCompletion,
    mission,
    isEmptyPeriod,
}) => {
    const i18n = useI18n();
    const { colors } = useTheme();
    const validateNumber = useValidateNumber();
    const { presentArticleId } = useIntercom();
    const formatDate = useDateFormatter();
    const hoursPerDay = useMemo(() => {
        return parseFloat(vacationStatus?.contractHoursPerWeekFullTime || "0") / 5;
    }, [vacationStatus]);

    const displayDefaultDaysValue = useCallback(
        (x?: number | undefined | null): string => {
            if (x === undefined || x === null) {
                return "";
            }
            return (x / 60 / hoursPerDay).toFixed(1);
        },
        [hoursPerDay],
    );

    const {
        control,
        watch,
        formState: { isValid },
        resetField,
        trigger,
    } = useForm<FormData>({
        mode: "onChange",
        reValidateMode: "onChange",
        defaultValues: {
            deltaWork: displayDefaultHoursValue(payrollBreakdown.deltaWork),
            usedVacationMinutes: displayDefaultDaysValue(payrollBreakdown.usedVacationMinutes),
            totalWorkedDays: displayDefaultNumberValue(payrollBreakdown.totalWorkedDays),
            deltaWorkMode: displayDefaultPlusMinusValue(payrollBreakdown.totalWorkedDays),
            absences: payrollBreakdown.absences || [],
            bonuses: payrollBreakdown.bonuses || [],
            effectiveFees: payrollBreakdown.effectiveFees || [],
            dynamicFees: payrollBreakdown.dynamicFees || [],
            overtimePaid100: ((payrollBreakdown.overtimePaid100 || 0) / 60).toString(),
            overtimePaid125: ((payrollBreakdown.overtimePaid125 || 0) / 60).toString(),
            totalWork125: ((payrollBreakdown.totalWork125 || 0) / 60).toString(),
            totalWork150: ((payrollBreakdown.totalWork150 || 0) / 60).toString(),
        },
    });
    const deltaWork = watch("deltaWork");
    const deltaWorkMode = watch("deltaWorkMode");
    const usedVacationMinutes = watch("usedVacationMinutes");
    const totalWorkedDays = watch("totalWorkedDays");
    const absences = watch("absences");
    const bonuses = watch("bonuses");
    const effectiveFees = watch("effectiveFees");
    const dynamicFees = watch("dynamicFees");
    const overtimePaid100 = watch("overtimePaid100");
    const overtimePaid125 = watch("overtimePaid125");
    const totalWork125 = watch("totalWork125");
    const totalWork150 = watch("totalWork150");

    const { data: overtimeData } = useQuery(GET_MISSION_OVERTIME_STATUS, {
        variables: {
            missionId: payrollBreakdown.missionInfo?.id,
        },
        skip: !payrollBreakdown?.missionInfo.id,
        fetchPolicy: "cache-and-network",
    });
    const { data: missionVacationAccumulatedMinutes } = useQuery(
        GET_MISSION_VACATION_ACCUMULATION_MINUTES_FOR_PAYROLL_BREAKDOWN,
        {
            variables: {
                payrollBreakdownId: payrollBreakdown?.id,
            },
            skip: !payrollBreakdown?.id,
            fetchPolicy: "cache-and-network",
        },
    );
    const endOfMonthVacationTotal = useMemo(() => {
        const availableInMinutes =
            (vacationStatus?.availableVacationMinutes || 0) +
            (missionVacationAccumulatedMinutes?.getMissionVacationAccumulationMinutesForPayrollBreakdown ||
                0);
        const availableInDays = availableInMinutes / 60 / hoursPerDay;

        return parseFloat((availableInDays - Number(usedVacationMinutes || 0)).toFixed(1));
    }, [usedVacationMinutes, vacationStatus, hoursPerDay, missionVacationAccumulatedMinutes]);

    const getDeltaWork = useCallback(() => {
        if (deltaWorkMode === "minus") {
            return Number(deltaWork) * -60;
        }
        return Number(deltaWork) * 60;
    }, [deltaWork, deltaWorkMode]);

    const endOfMonthOvertimeTotal = useMemo(() => {
        const overtime = overtimeData?.getMissionOvertimeStatus.overtimeMinutes;
        if (overtime === undefined) {
            return null;
        }
        return parseFloat((overtime + Number(getDeltaWork() || 0)).toFixed(1));
    }, [overtimeData, getDeltaWork]);

    const formattedEndOfMonthOvertimeTotal = useMemo(
        () => (endOfMonthOvertimeTotal || 0) / 60,
        [endOfMonthOvertimeTotal],
    );

    const overtimePaidNumber = useMemo(
        () => Number(overtimePaid100 || "0") + Number(overtimePaid125 || "0"),
        [overtimePaid100, overtimePaid125],
    );

    const overtimeWorkDelta = useMemo(
        () => Number((formattedEndOfMonthOvertimeTotal - overtimePaidNumber).toFixed(1)),
        [formattedEndOfMonthOvertimeTotal, overtimePaidNumber],
    );

    useDeepCompareEffect(() => {
        const data = {
            deltaWork: getDeltaWork(),
            usedVacationMinutes: usedVacationMinutes
                ? Math.round(Number(usedVacationMinutes) * 60 * hoursPerDay)
                : 0,
            totalWorkedDays: needWorkDays ? Number(totalWorkedDays) : undefined,
            absences,
            bonuses: bonuses,
            effectiveFees: effectiveFees,
            dynamicFees: dynamicFees.map((f) => ({ refId: f.fee.id, count: f.count || 0 })),
            overtimePaid100: overtimePaid100 ? Number(overtimePaid100) * 60 : 0,
            overtimePaid125: overtimePaid125 ? Number(overtimePaid125) * 60 : 0,
            totalWork125: totalWork125 ? Number(totalWork125) * 60 : 0,
            totalWork150: totalWork150 ? Number(totalWork150) * 60 : 0,
        };
        onChange(data);
        if (isValid) {
            onValidData(data);
        } else {
            onValidData(null);
        }
    }, [
        isValid,
        deltaWork,
        deltaWorkMode,
        usedVacationMinutes,
        totalWorkedDays,
        onValidData,
        onChange,
        needWorkDays,
        hoursPerDay,
        getDeltaWork,
        absences,
        bonuses,
        effectiveFees,
        dynamicFees,
        overtimePaid100,
        overtimePaid125,
        totalWork125,
        totalWork150,
    ]);

    const {
        append: appendBonus,
        remove: removeBonusAtIndex,
        update: updateBonus,
    } = useFieldArray({
        name: "bonuses",
        control,
    });

    const { append: appendAbsence, remove: removeAbsenceAtIndex } = useFieldArray({
        name: "absences",
        control,
    });

    const {
        append: appendEffectiveFee,
        remove: removeEffectiveFeeAtIndex,
        update: updateEffectiveFee,
    } = useFieldArray({
        name: "effectiveFees",
        control,
    });

    const { chainInputFocus } = useChainInputFocus();

    const validateDeltaWork = useCallback(
        (value?: string) => {
            if (!value) {
                return undefined;
            }
            return validateNumber(value);
        },
        [validateNumber],
    );

    const maxWorkDays = useMemo(() => {
        if (!mission?.currentContract?.startDate || !payrollBreakdown) {
            return 0;
        }
        return calculatePayrollBreakdownMaxWorkDays({
            contractStart: mission.currentContract.startDate,
            contractEnd: mission.currentContract.endDate,
            payrollBreakdownStart: payrollBreakdown.startDate,
            payrollBreakdownEnd: payrollBreakdown.endDate,
        });
    }, [mission?.currentContract?.endDate, mission?.currentContract?.startDate, payrollBreakdown]);

    const payrollBreakdownBonusEffectiveFeesOvertimePaidProps = useMemo(
        () => ({
            control: control,
            resetField: resetField,
            bonuses: {
                onCreate: appendBonus,
                onUpdate: updateBonus,
                onDeleteAtIndex: removeBonusAtIndex,
                clientDeductions: mission?.currentContract?.clientDeductions || [],
            },
            effectiveFees: {
                onCreate: appendEffectiveFee,
                onUpdate: updateEffectiveFee,
                onDeleteAtIndex: removeEffectiveFeeAtIndex,
            },
            timeSheetsNeedCompletion: timeSheetsNeedCompletion,
            overtime: {
                overtimeWorkDelta: overtimeWorkDelta,
                hourlyInvoiceAmount:
                    ((overtimeData?.getMissionOvertimeStatus.overtimeHourlyInvoiceAmount || 0) *
                        (overtimeData?.getMissionOvertimeStatus.overtimePaidAtPercent || 100)) /
                    100,
                formattedEndOfMonthOvertimeTotal,
                overtimePaidAtPercent:
                    overtimeData?.getMissionOvertimeStatus.overtimePaidAtPercent || 100,
            },
        }),
        [
            appendBonus,
            removeBonusAtIndex,
            updateBonus,
            appendEffectiveFee,
            control,
            formattedEndOfMonthOvertimeTotal,
            mission?.currentContract?.clientDeductions,
            overtimeData?.getMissionOvertimeStatus.overtimeHourlyInvoiceAmount,
            overtimeData?.getMissionOvertimeStatus.overtimePaidAtPercent,
            overtimeWorkDelta,
            removeEffectiveFeeAtIndex,
            resetField,
            timeSheetsNeedCompletion,
            updateEffectiveFee,
        ],
    );

    useEffect(() => {
        void trigger("overtimePaid100");
    }, [deltaWork, trigger]);

    return (
        <VStack>
            {isFromTimesheet ? (
                <>
                    <VStack h="2/3">
                        <PayrollBreakdownMonthlyInfo
                            payrollBreakdown={payrollBreakdown}
                            disabled={timeSheetsNeedCompletion}
                        />
                        <Gap gap={0.25} />
                        <PayrollBreakdownTotalInfo
                            payrollBreakdown={payrollBreakdown}
                            vacationStatus={vacationStatus}
                            disabled={timeSheetsNeedCompletion}
                        />
                        <Gap gap={1} />
                        <FullWidthLine />
                        <DynamicFees
                            control={control}
                            dynamicFees={dynamicFees}
                            clientDeductions={mission?.currentContract?.clientDeductions || []}
                            chainInputFocus={chainInputFocus}
                        />
                        <Gap gap={1} />
                        <PayrollBreakdownBonusEffectiveFeesOvertimePaid
                            {...payrollBreakdownBonusEffectiveFeesOvertimePaidProps}
                        />
                    </VStack>
                    <PayrollBreakdownAbsences absences={payrollBreakdown.absences} />
                </>
            ) : (
                <VStack h="2/3">
                    {!isEmptyPeriod && (
                        <>
                            <BodyMediumText>
                                {i18n.t("payroll_breakdown.field_delta_work")}
                            </BodyMediumText>
                            <Gap gap={0.5} />
                            <HStack fit="center" gap={0}>
                                <Controller
                                    name="deltaWorkMode"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <DropdownField
                                            value={value}
                                            onSelect={onChange}
                                            items={[
                                                {
                                                    label: i18n.t(
                                                        "payroll_breakdown.field_delta_work_mode_plus",
                                                    ),
                                                    value: "plus",
                                                },
                                                {
                                                    label: i18n.t(
                                                        "payroll_breakdown.field_delta_work_mode_minus",
                                                    ),
                                                    value: "minus",
                                                },
                                            ]}
                                            style={{ flex: 1, flexGrow: 2, flexShrink: 1 }}
                                        />
                                    )}
                                />
                                <Gap gap={1} />
                                <Controller
                                    name="deltaWork"
                                    control={control}
                                    rules={{
                                        validate: {
                                            isNumber: validateDeltaWork,
                                        },
                                    }}
                                    render={({
                                        field: { onChange, value },
                                        fieldState: { error },
                                    }) => (
                                        <TextField
                                            {...chainInputFocus(0)}
                                            value={value}
                                            onChangeText={onChange}
                                            error={!!error?.message}
                                            showErrorText={false}
                                            isDecimalNumber
                                            keyboardType="numeric"
                                            returnKeyType="done"
                                            selectTextOnFocus
                                            style={{
                                                flex: 0,
                                                flexGrow: 1,
                                                maxWidth: 80,
                                                minWidth: 80,
                                            }} // minWidth is needed to prevent problem of focus on web
                                            inputStyle={{
                                                textAlign: "right",
                                                width: undefined,
                                            }}
                                        />
                                    )}
                                />
                            </HStack>
                            <Gap gap="2/3" />
                            <FullWidthLine />
                            <Gap gap="2/3" />
                            <BodyMediumText>
                                {i18n.t("payroll_breakdown.overtime_paid_125_150.title")}
                            </BodyMediumText>
                            <Gap gap="1/3" />
                            <>
                                <Controller
                                    name="totalWork125"
                                    control={control}
                                    rules={{
                                        required: false,
                                        validate: validateNumber,
                                    }}
                                    render={({
                                        field: { onChange, onBlur, value },
                                        fieldState: { error },
                                    }) => (
                                        <TextField
                                            {...chainInputFocus(2)}
                                            isSelfContained
                                            style={{
                                                borderBottomRightRadius: 0,
                                                borderBottomLeftRadius: 0,
                                            }}
                                            label={i18n.t("payroll_breakdown.overtime_125")}
                                            value={value}
                                            onChangeText={onChange}
                                            onBlur={onBlur}
                                            error={error?.message}
                                            isDecimalNumber
                                            keyboardType="numeric"
                                            returnKeyType="done"
                                            selectTextOnFocus
                                            inputStyle={{
                                                textAlign: "right",
                                                width: undefined,
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name="totalWork150"
                                    control={control}
                                    rules={{
                                        required: false,
                                        validate: validateNumber,
                                    }}
                                    render={({
                                        field: { onChange, onBlur, value },
                                        fieldState: { error },
                                    }) => (
                                        <TextField
                                            {...chainInputFocus(3)}
                                            isSelfContained
                                            style={{
                                                marginTop: -2,
                                                borderTopRightRadius: 0,
                                                borderTopLeftRadius: 0,
                                            }}
                                            label={i18n.t("payroll_breakdown.overtime_150")}
                                            value={value}
                                            onChangeText={onChange}
                                            onBlur={onBlur}
                                            error={error?.message}
                                            isDecimalNumber
                                            keyboardType="numeric"
                                            returnKeyType="done"
                                            selectTextOnFocus
                                            inputStyle={{
                                                textAlign: "right",
                                                width: undefined,
                                            }}
                                        />
                                    )}
                                />
                            </>
                            <Gap gap="1/3" />
                            <BodySmallText color={colors.greyAccessible}>
                                {i18n.t("payroll_breakdown.overtime_paid_125_150.description")}
                            </BodySmallText>
                            <LinkSmallText
                                onPress={() => {
                                    void presentArticleId("10485414");
                                }}
                            >
                                {i18n.t("payroll_breakdown.overtime_paid_125_150.link")}
                            </LinkSmallText>
                            <Gap gap={1} />
                            <FullWidthLine />
                            <Gap gap={1} />
                            <PayrollBreakdownCommon
                                fees={mission?.currentContract?.fees || []}
                                deductions={mission?.currentContract?.deductions || []}
                                needWorkDays={needWorkDays}
                                control={control}
                                chainInputFocus={(index) => chainInputFocus(4 + index)}
                                maxWorkDays={maxWorkDays}
                            />
                            <BodyMediumText>
                                {i18n.t("payroll_breakdown.field_total_vacation_days_title")}
                            </BodyMediumText>
                            <Gap gap={0.5} />
                            <Controller
                                name="usedVacationMinutes"
                                control={control}
                                rules={{ validate: { isNumber: validateNumber } }}
                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                    <TextField
                                        {...chainInputFocus(5)}
                                        label={i18n.t(
                                            "payroll_breakdown.field_total_vacation_days",
                                        )}
                                        value={value}
                                        onChangeText={onChange}
                                        error={error?.message}
                                        showErrorText
                                        isDecimalNumber
                                        keyboardType="numeric"
                                        selectTextOnFocus
                                        returnKeyType="done"
                                        style={{ alignSelf: "flex-start" }}
                                        inputStyle={{
                                            textAlign: "right",
                                            width: undefined,
                                            minWidth: 0,
                                        }}
                                    />
                                )}
                            />
                            <Gap gap="2/3" />
                            <BodySmallText color={colors.greyAccessible}>
                                {i18n.t("payroll_breakdown.used_vacation_days_info", {
                                    count: endOfMonthVacationTotal,
                                    date: formatDate(
                                        new Date(payrollBreakdown.endDate),
                                        SHORT_DATE,
                                    ),
                                })}
                            </BodySmallText>
                            <Gap gap="2/3" />
                            <FullWidthLine />
                            <Gap gap="2/3" />
                            <PayrollBreakdownBonusEffectiveFeesOvertimePaid
                                {...payrollBreakdownBonusEffectiveFeesOvertimePaidProps}
                            />
                            <DynamicFees
                                control={control}
                                dynamicFees={dynamicFees}
                                clientDeductions={mission?.currentContract?.clientDeductions || []}
                                chainInputFocus={(i) => chainInputFocus(6 + i)}
                            />
                            <Gap gap="2/3" />
                        </>
                    )}
                    <VStack gap="1/3">
                        {!isEmptyPeriod && (
                            <>
                                <TitleMediumText>{i18n.t("absence.title")}</TitleMediumText>
                                <>
                                    <AbsenceList
                                        absences={absences}
                                        onDeleteAbsenceAtIndex={removeAbsenceAtIndex}
                                    />
                                    <AbsenceCreateButton
                                        onCreate={appendAbsence}
                                        minDate={
                                            payrollBreakdown.startDate
                                                ? new Date(payrollBreakdown.startDate)
                                                : undefined
                                        }
                                        maxDate={
                                            payrollBreakdown.endDate
                                                ? new Date(payrollBreakdown.endDate)
                                                : undefined
                                        }
                                    />
                                </>
                                <Gap gap="2/3" />
                            </>
                        )}
                    </VStack>
                </VStack>
            )}
            <Gap gap={2} />
        </VStack>
    );
};

export default PayrollBreakdownEditorMonthly;
